import { system, LookupStorage } from "portalframework";
import * as ko from "knockout";
import * as _ from "lodash";
import * as entities from "entities";
import { personExtended, UserSearchCriteria, person_role, team_member } from "organized";
import { BaseService } from "./_common/_baseService";
import { portalHttpService } from "./_common/httpService";

class TechnicianService extends BaseService {
  constructor() {
    super("technician");
  }

  public getUsersListScreenData = async (): Promise<any> => {
    let requiredLookups = ["C_PERSON_REGISTRATION_STATUS", "PERSON_STATUS"];
    let lookupsToFetch = _.difference(requiredLookups, LookupStorage.lookupStorage.lookupNames());
    let data1 = ko.toJSON({ requiredLookups: lookupsToFetch });
    const data = await portalHttpService.post<any>(
      system.apiURL() + "api/" + this.controllerName + "/UserListScreenData",
      data1,
    );
    _.each(lookupsToFetch, (lookupName) => {
      let list = data.result.results[lookupName];
      if (list) {
        LookupStorage.lookupStorage.add(new LookupStorage.Lookup(lookupName, list));
      }
    });
    let reqs;
    if (data.result.results && data.result.results.request) {
      reqs = entities.loadRelation(data.result.results.product, "product", "array")();
    }
    return new entities.SearchResult({ data: reqs, count: data.count });
  };

  public searchUsers = async (criteria: UserSearchCriteria): Promise<entities.SearchResult> => {
    let requiredLookups = ["PERSON_STATUS"];
    let lookupsToFetch = _.difference(requiredLookups, LookupStorage.lookupStorage.lookupNames());
    let data = ko.toJSON({ ...criteria, requiredLookups: lookupsToFetch });

    const result = await portalHttpService.post<any>(`${this.baseApiUrl()}/SearchUsers`, data);

    let list;
    _.each(requiredLookups, (lookupName) => {
      list = result.result.results[lookupName];
      if (list) {
        LookupStorage.lookupStorage.add(new LookupStorage.Lookup(lookupName, list));
      }
    });

    return new entities.SearchResult({
      data: entities.loadEntities(result.result.results.person, "person")(),
      count: result.count,
      lookups: list,
    });
  };

  public getUser = async (
    person_id?,
  ): Promise<{
    user: personExtended;
    roles: KnockoutObservableArray<entities.user_role>;
    lookups: any;
    teamMember: KnockoutObservableArray<team_member>;
  }> => {
    let requiredLookups = ["C_PERSON_REGISTRATION_STATUS", "PERSON_STATUS"];
    let data = ko.toJSON({ person_id: person_id, requiredLookups: requiredLookups });

    const result = await portalHttpService.post<any>(system.apiURL() + "api/" + this.controllerName + "/GetUser", data);

    let list;
    _.each(requiredLookups, (lookupName) => {
      list = result.results[lookupName];
      if (list) {
        LookupStorage.lookupStorage.add(new LookupStorage.Lookup(lookupName, list));
      }
    });
    let person: personExtended = null;
    if (result.results && result.results.person) {
      person = new personExtended(result.results.person.person);
    }
    let user_roles: KnockoutObservableArray<entities.user_role>;
    if (result.results && result.results.user_role) {
      user_roles = entities.loadEntities(result.results.user_role, "user_role");
    }
    let team_member: KnockoutObservableArray<team_member>;
    if (result.results && result.results.team) {
      if (result.results.team["row"]) {
        team_member = ko.observableArray();
        let row = result.results.team["row"];
        let transformedArray;
        let teamObj;
        if (row && Array.isArray(row)) {
          transformedArray = row.map((obj) => {
            let innerArr = obj.attr;
            let teamId = innerArr[0].value;
            let description = innerArr[1].value;
            if (description == "") {
              description = innerArr[0].value;
            }
            return { team_id: teamId, description: description };
          });
          teamObj = { team: transformedArray.filter((team) => team.team_id.trim() != "") };
        } else if (row && typeof row === "object" && Array.isArray(row.attr)) {
          let teamId = row.attr[0].value;
          let description = row.attr[1].value;
          if (description == "") {
            description = row.attr[0].value;
          }
          transformedArray = [{ team_id: teamId, description: description }];
          teamObj = { team: transformedArray.filter((team) => team.team_id.trim() != "") };
        }
        team_member = entities.loadEntities(teamObj, "team");
      } else {
        team_member = entities.loadEntities({ team: [] }, "team");
      }
    }
    return { user: person, roles: user_roles, lookups: list, teamMember: team_member };
  };

  public getUserPlaceIds = async (): Promise<{ value: string; isDefault: boolean }[]> => {
    return await portalHttpService.get(system.apiURL() + "api/" + this.controllerName + "/GetUserPlaceIds");
  };

  public getUserPredefinedTeamsAndRoles = async (
    place_id: string = "empty",
  ): Promise<{
    person: personExtended;
    placeIdList: string[];
    defaultPlaceId: string;
  }> => {
    const params = {
      place_id,
    };
    const result = await portalHttpService.get<any>(
      system.apiURL() + "api/" + this.controllerName + "/GetUserPredefinedTeamsAndRoles",
      params,
    );
    if (result) {
      return {
        person: new personExtended(result),
        placeIdList: result.place_id_list,
        defaultPlaceId: result.default_place_id,
      };
    }
  };

  public getTeamUsers = async (
    teamId: string,
    placeId: string,
    status: string,
  ): Promise<{ peopleIn: personExtended[]; peopleOut: personExtended[] }> => {
    let data = ko.toJSON({ teamId, placeId, status });
    const result = await portalHttpService.post<any>(
      system.apiURL() + "api/" + this.controllerName + "/GetTeamUsers",
      data,
    );

    let peopleIn: personExtended[];
    let peopleOut: personExtended[];
    if (result?.results?.person1?.person) {
      if (Array.isArray(result.results.person1.person)) {
        peopleIn = _.map(result.results.person1.person, (o) => {
          return new personExtended(o);
        });
      } else {
        peopleIn = [new personExtended(result.results.person1.person)];
      }
    }
    if (result?.results?.person2?.person) {
      if (Array.isArray(result.results.person2.person)) {
        peopleOut = _.map(result.results.person2.person, (o) => {
          return new personExtended(o);
        });
      } else {
        peopleOut = [new personExtended(result.results.person2.person)];
      }
    }
    return { peopleIn, peopleOut };
  };

  public saveTeamUsers = async (team_id: string, users: any): Promise<void> => {
    let data = ko.toJSON({ team_id: team_id, users: users });
    await portalHttpService.post<any>(system.apiURL() + "api/" + this.controllerName + "/SaveTeamUsers", data);
  };

  public getRoleUsers = async (
    selectedRole: string,
    placeId: string,
    status: string,
  ): Promise<{ peopleIn: personExtended[]; peopleOut: personExtended[] }> => {
    let data = ko.toJSON({ selectedRole, placeId, status });
    const result = await portalHttpService.post<any>(
      system.apiURL() + "api/" + this.controllerName + "/GetRoleUsers",
      data,
    );
    let peopleIn: personExtended[];
    let peopleOut: personExtended[];
    if (result?.results?.person1?.person) {
      if (Array.isArray(result.results.person1.person)) {
        peopleIn = _.map(result.results.person1.person, (o) => {
          return new personExtended(o);
        });
      } else {
        peopleIn = [new personExtended(result.results.person1.person)];
      }
    }
    if (result?.results?.person2?.person) {
      if (Array.isArray(result.results.person2.person)) {
        peopleOut = _.map(result.results.person2.person, (o) => {
          return new personExtended(o);
        });
      } else {
        peopleOut = [new personExtended(result.results.person2.person)];
      }
    }
    return { peopleIn, peopleOut };
  };

  public saveRoleUsers = async (user_role: string, users: any): Promise<void> => {
    let data = ko.toJSON({ user_role: user_role, users: users });
    await portalHttpService.post<any>(system.apiURL() + "api/" + this.controllerName + "/SaveRoleUsers", data);
  };

  public addrole = async (person_id: string, user_role: string): Promise<person_role> => {
    let data = ko.toJSON({ person_id: person_id, user_role: user_role });
    const result = await portalHttpService.post<any>(system.apiURL() + "api/" + this.controllerName + "/addrole", data);

    return new person_role(result.person_role.person_role);
  };

  public addTeam = async (person_id: string, teamMember: string): Promise<team_member> => {
    let data = ko.toJSON({ person_id: person_id, team_id: teamMember });
    const result = await portalHttpService.post<any>(system.apiURL() + "api/" + this.controllerName + "/addteam", data);
    return new team_member(result.team_member.team_member);
  };

  public removeTeam = async (person_id: string, teamMember: team_member): Promise<void> => {
    let data = ko.toJSON({ person_id: person_id, team_id: teamMember.team_id });
    await portalHttpService.post<any>(system.apiURL() + "api/" + this.controllerName + "/removeTeam", data);
  };

  public inactiveuser = async (person_id: string): Promise<any> => {
    let data = ko.toJSON({ person_id: person_id });

    return await portalHttpService.post<any>(system.apiURL() + "api/" + this.controllerName + "/inactiveuser", data);
  };

  public updateUser = async (userParams: any): Promise<void> => {
    await portalHttpService.post<any>(system.apiURL() + "api/" + this.controllerName + "/updateUser", userParams);
  };
}

export const technicianService = new TechnicianService();
